import { twJoin } from "tailwind-merge";

import { CheckCircleFilledIcon } from "@/components/Icons/CheckCircleFilledIcon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { ClearInputIcon } from "@/components/Icons/ClearInputIcon";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { useTranslation } from "@/translations";

export function PasswordHelper({
  password,
  hasSubmittedPassword,
}: {
  password: string;
  hasSubmittedPassword: boolean;
}): React.ReactNode {
  const { t } = useTranslation();
  const rules = [
    {
      translation: t("page.login.errors.password-minimum-chars", {
        chars: 8,
      }),
      fn: (e) => e.length >= 8,
    },
    {
      translation: t("page.login.errors.password-lowercase"),
      fn: (e) => e.toUpperCase() !== e,
    },
    {
      translation: t("page.login.errors.password-number"),
      fn: (e) => /\d/.test(e),
    },
  ] satisfies {
    translation: string;
    fn: (e: string) => boolean;
  }[];
  const anyFailed = hasSubmittedPassword && rules.some((rule) => !rule.fn(password));

  return (
    <Stack>
      <p
        className={twJoin(
          "mt-space-sm mb-space-xs text-left text-overline",
          anyFailed ? "text-text-danger-secondary" : "",
        )}
      >
        {t("page.login.errors.password-requirements")}
      </p>
      <Stack gap="xs">
        {rules.map((rule) => {
          const match = rule.fn(password);
          let state: "success" | "notMatchingYet" | "failed";
          if (match) {
            state = "success";
          } else if (!hasSubmittedPassword) {
            state = "notMatchingYet";
          } else {
            state = "failed";
          }
          const stateToneClassName = twJoin(
            state === "success" && "text-text-success",
            state === "notMatchingYet" && "text-text-primary",
            state === "failed" && "text-text-danger-secondary",
          );
          let stateLabel: string;
          if (state === "success") {
            stateLabel = t("page.login.password-requirement-status.success");
          } else if (state === "notMatchingYet") {
            stateLabel = t("page.login.password-requirement-status.not-matching-yet");
          } else {
            stateLabel = t("page.login.password-requirement-status.failed");
          }

          return (
            <Inline key={rule.translation} gap="xs" align="center">
              <span className={twJoin("flex size-icon-inline items-center justify-center", stateToneClassName)}>
                <span className="sr-only">{stateLabel}</span>
                {state === "success" && <CheckCircleFilledIcon size={14} />}
                {state === "notMatchingYet" && <CheckIcon />}
                {state === "failed" && <ClearInputIcon size={14} />}
              </span>
              <span className={twJoin("text-overline", stateToneClassName)}>{rule.translation}</span>
            </Inline>
          );
        })}
      </Stack>
    </Stack>
  );
}
